import { AntDesign, Ionicons, MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Copy, RefreshCw, X } from "lucide-react-native";
import { Box, Button, HStack, Icon, IconButton, Image, Pressable, Spinner, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { Text as RNText, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";

import { isDesktopScreen, Scale } from "../constants";
import { HORMONE_HEX_COLOR } from "../constants/theme";
import { findFoodBySuggestedServingId, findMatchingSuggestedServing } from "../helpers/diaryHelpers";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { getServingDescriptionText } from "../helpers/foodHelpers";
import {
  formatMealType,
  formatNumberAsWholeNumber,
  getRecipeMealImage,
  getSimpleRecipeImage,
} from "../helpers/generalHelpers";
import { getTotalRecipeTime } from "../helpers/simpleRecipeHelpers";
import { getTestIDForRecipeMeal, getTestIDForSimpleRecipeMeal } from "../helpers/testHelpers";
import {
  doesOrganisationHaveManagedPlanning,
  getOrganisation,
  shouldShowHormoneIndicator,
} from "../helpers/userHelpers";
import styles from "../screens/DiaryViewScreenStyles";
import type {
  CalendarItemContentTypeEnum,
  CalendarItemStatusEnum,
  Meal,
  MealMomentEnum,
  QuickAddMeal,
  RecipeMeal,
  SimpleRecipeMeal,
  SingleFoodMeal,
} from "../services/backendTypes";
import { FoodById, foodSelector } from "../slices/foodSlice";
import { userSelector, viewAsUserSelector } from "../slices/userSlice";

type CalendarItemComponentBaseProps = {
  onPress: () => void;
  onLongPress: () => void;
  // NOTE: This is kept because it may be used in the near future
  // eslint-disable-next-line react-redux/no-unused-prop-types
  testID?: string;
  onPressCalendarItemPlannedStatus?: () => void;
  editable?: boolean;
};

interface CalendarItemComponentProps extends CalendarItemComponentBaseProps {
  meal: Meal;
  status: CalendarItemStatusEnum;
  onSwap?: () => Promise<void>;
  contentType: CalendarItemContentTypeEnum;
  isLoading: boolean;
  editable?: boolean;
  onListAlternativesPress: () => void;
  mealMoment?: MealMomentEnum;
  onPressCopyMeal?: () => void;
}

interface SingleFoodOrQuickAddMealPlannerItemProps extends CalendarItemComponentBaseProps {
  isEaten: boolean;
  title: string;
  description: string;
}

interface RecipeMealPlannerItemProps extends CalendarItemComponentBaseProps {
  isEaten: boolean;
  recipeMeal: RecipeMeal;
  isLoading: boolean;
  onSwap?: () => void;
  onPressCalendarItemPlannedStatus: () => void;
  onListAlternativesPress: () => void;
  mealMoment?: MealMomentEnum;
  onPressCopyMeal?: () => void;
}

interface SimpleRecipeMealPlannerItemProps extends CalendarItemComponentBaseProps {
  isEaten: boolean;
  simpleRecipeMeal: SimpleRecipeMeal;
  isLoading: boolean;
  onSwap?: () => void;
  onPressCalendarItemPlannedStatus: () => void;
  onListAlternativesPress: () => void;
  mealMoment?: MealMomentEnum;
  onPressCopyMeal?: () => void;
}

const CheckboxComponent = ({
  onPress,
  checked,
  testID,
}: {
  onPress: () => void;
  checked: boolean;
  testID?: string;
}): JSX.Element => {
  const theme = useTheme();

  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const onPressCheckbox = (): void => {
    setSubmitted(true);
    onPress();

    setTimeout(() => {
      setSubmitted(false);
    }, 750);
  };

  return (
    // NOTE: The Checkbox does not update when the checked prop changes
    // (probably something to do with it being a controlled form component).
    // As such, we wrap it in a View for use in the tests
    <>
      {!submitted ? (
        <TouchableOpacity onPress={onPressCheckbox} testID={testID}>
          <MaterialCommunityIcons
            name={checked ? "checkbox-marked-circle" : "checkbox-blank-circle-outline"}
            size={36}
            color={checked ? theme.colors.primary["600"] : theme.colors.gray["700"]}
          />
        </TouchableOpacity>
      ) : (
        <Spinner size={36} />
      )}
    </>
  );
};

const RecipeMealPlannerItem = ({
  isEaten,
  recipeMeal,
  onPress,
  onSwap,
  onLongPress,
  onPressCalendarItemPlannedStatus,
  onListAlternativesPress,
  isLoading = false,
  editable = true,
  mealMoment,
  onPressCopyMeal,
}: RecipeMealPlannerItemProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useSelector(userSelector);
  const shouldHideMacros = isFeatureFlagActive(user, FeatureFlag.FF_DONT_SHOW_MACROS);
  const shouldShowKcal = !isFeatureFlagActive(user, FeatureFlag.FF_RECIPE_INSPIRATION);

  const showHormoneIndicator = shouldShowHormoneIndicator(user, recipeMeal.recipe_template.tags);

  return (
    <Box borderRadius={theme.borderRadii.card.default} overflow="hidden" bg="white" mx={0} shadow="10">
      <Pressable onPress={onPress} onLongPress={onLongPress} testID={getTestIDForRecipeMeal(recipeMeal)}>
        <VStack>
          {/* Image Container - Top with padding */}
          <Box p={3}>
            <Box
              position="relative"
              width="100%"
              height={180}
              borderRadius={theme.borderRadii.card.default}
              overflow="hidden"
            >
              <Image
                source={getRecipeMealImage(recipeMeal).source}
                resizeMode="cover"
                alt={"Recipe img"}
                width="100%"
                height="100%"
                opacity={isEaten ? 0.6 : 1}
              />

              {/* Hormone indicator */}
              {showHormoneIndicator ? (
                <Box
                  position="absolute"
                  top={2}
                  right={2}
                  bg="white"
                  borderRadius={theme.borderRadii.badge.pill}
                  p={1.5}
                  shadow={2}
                  borderWidth={1.5}
                  borderColor={HORMONE_HEX_COLOR}
                  testID="hormoneIndicator"
                >
                  <Icon as={Ionicons} name="female" size="xs" color={HORMONE_HEX_COLOR} />
                </Box>
              ) : null}

              {/* Centered Meal Type Text */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                justifyContent="center"
                alignItems="center"
                pointerEvents="none"
              >
                <RNText
                  style={{
                    fontSize: 30,
                    fontFamily: theme.fonts.serif,
                    fontStyle: "italic",
                    color: "white",
                    fontWeight: "400",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowOffset: { width: 1, height: 1 },
                    textShadowRadius: 3,
                  }}
                >
                  {mealMoment ? formatMealType(mealMoment, t) : ""}
                </RNText>
              </Box>

              {/* Loading spinner overlay */}
              {isLoading ? (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  justifyContent="center"
                  alignItems="center"
                  bg="rgba(255, 255, 255, 0.8)"
                >
                  <Spinner size="lg" color="primary.500" />
                </Box>
              ) : null}
            </Box>
          </Box>

          {/* Content Container - Bottom */}
          <VStack px={3} pb={3} space={1}>
            <Text fontSize="lg" fontWeight="700" color="gray.900" strikeThrough={isEaten} numberOfLines={2}>
              {recipeMeal.recipe_template.name}
            </Text>

            <HStack alignItems="center" space={1}>
              <Text fontSize="md" color="gray.500" fontWeight="500" strikeThrough={isEaten}>
                {formatMealType(mealMoment || "BREAKFAST", t)}, {recipeMeal.recipe_template.preparation_time_min} mins
              </Text>
            </HStack>

            {shouldShowKcal && !shouldHideMacros ? (
              <HStack alignItems="center" space={1} mt={1}>
                <Text fontSize="md" color="gray.600" fontWeight="600" strikeThrough={isEaten}>
                  {formatNumberAsWholeNumber(recipeMeal.kcal)} {t("general.kcal")}
                </Text>
              </HStack>
            ) : null}

            {/* Action Buttons */}
            <HStack space={4} mt={4} justifyContent="flex-end">
              <IconButton
                onPress={onLongPress}
                icon={<Icon as={MaterialIcons} name="delete" size="md" color="white" />}
                testID={`delete-recipeMeal-${recipeMeal.id}`}
                variant="ghost"
                borderRadius="full"
                bg="primary.50"
                _icon={{
                  color: "white",
                }}
                _pressed={{
                  bg: "primary.50",
                }}
                size="md"
                p={2}
              />

              {onPressCopyMeal ? (
                <IconButton
                  onPress={onPressCopyMeal}
                  icon={<Icon as={MaterialCommunityIcons} name="content-copy" size="md" color="white" />}
                  testID={`copy-recipeMeal-${recipeMeal.id}`}
                  variant="ghost"
                  borderRadius="full"
                  bg="primary.600"
                  _icon={{
                    color: "white",
                  }}
                  _pressed={{
                    bg: "primary.700",
                  }}
                  size="md"
                  p={2}
                />
              ) : null}

              <IconButton
                onPress={onListAlternativesPress}
                icon={<Icon as={AntDesign} name="sync" size="md" color="white" />}
                testID={`swap-recipeMeal-${recipeMeal.id}`}
                variant="ghost"
                borderRadius="full"
                bg="primary.50"
                _icon={{
                  color: "white",
                }}
                _pressed={{
                  bg: "primary.50",
                }}
                size="md"
                p={2}
              />
            </HStack>
          </VStack>
        </VStack>
      </Pressable>
    </Box>
  );
};

const SimpleRecipeMealPlannerItem = ({
  isEaten,
  simpleRecipeMeal,
  onPress,
  onSwap,
  onLongPress,
  onPressCalendarItemPlannedStatus,
  onListAlternativesPress,
  isLoading = false,
  editable = true,
  mealMoment,
  onPressCopyMeal,
}: SimpleRecipeMealPlannerItemProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      borderRadius={theme.borderRadii.card.default}
      overflow="hidden"
      mx={0}
      shadow={10}
      position="relative"
      height={353}
    >
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        testID={getTestIDForSimpleRecipeMeal(simpleRecipeMeal)}
        flex={1}
      >
        {/* Full Coverage Background Image - Unblurred */}
        <Image
          source={getSimpleRecipeImage(simpleRecipeMeal).source}
          resizeMode="cover"
          alt={"Recipe img"}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          width="100%"
          height="100%"
          opacity={isEaten ? 0.6 : 1}
        />

        {/* Layered blur effect - focused on bottom portion, keeping middle clear */}
        {/* Layer 1: Very light blur, bottom 25% */}
        <Box position="absolute" left={0} right={0} bottom={0} height="25%" overflow="hidden">
          <Image
            source={getSimpleRecipeImage(simpleRecipeMeal).source}
            resizeMode="cover"
            alt={""}
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            width="100%"
            height={353}
            opacity={isEaten ? 0.2 : 0.3}
            blurRadius={2}
          />
        </Box>

        {/* Layer 2: Medium blur, bottom 15% */}
        <Box position="absolute" left={0} right={0} bottom={0} height="15%" overflow="hidden">
          <Image
            source={getSimpleRecipeImage(simpleRecipeMeal).source}
            resizeMode="cover"
            alt={""}
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            width="100%"
            height={353}
            opacity={isEaten ? 0.3 : 0.5}
            blurRadius={3}
          />
        </Box>

        {/* Layer 3: Strong blur, bottom 10% only */}
        <Box position="absolute" left={0} right={0} bottom={0} height="10%" overflow="hidden">
          <Image
            source={getSimpleRecipeImage(simpleRecipeMeal).source}
            resizeMode="cover"
            alt={""}
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            width="100%"
            height={353}
            opacity={isEaten ? 0.4 : 0.6}
            blurRadius={4}
          />
        </Box>

        {/* Gradient Overlay for Text Readability - lighter version */}
        <LinearGradient
          colors={["transparent", "rgba(0,0,0,0.2)", "rgba(0,0,0,0.3)"]}
          locations={[0.4, 0.7, 1]}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "60%",
          }}
        />

        {/* Center Meal Type Text */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          justifyContent="center"
          alignItems="center"
          pointerEvents="none"
        >
          <RNText
            style={{
              fontSize: 30,
              fontFamily: theme.fonts.serif,
              fontStyle: "italic",
              color: "white",
              fontWeight: "400",
              textShadowColor: "rgba(0, 0, 0, 0.6)",
              textShadowOffset: { width: 2, height: 2 },
              textShadowRadius: 4,
              textDecorationLine: isEaten ? "line-through" : "none",
            }}
          >
            {mealMoment ? formatMealType(mealMoment, t) : ""}
          </RNText>
        </Box>

        {/* Text Overlay Container - Bottom Left */}
        <VStack position="absolute" bottom={3} left={2} space={0.5} maxWidth="65%" pr={2}>
          {/* Recipe Name */}
          <Text
            fontSize={18}
            fontStyle="italic"
            color="function.10"
            fontFamily={theme.fonts.serif}
            lineHeight={20}
            numberOfLines={2}
            strikeThrough={isEaten}
          >
            {simpleRecipeMeal.simple_recipe.name}
          </Text>

          {/* Meal Type and Preparation Time */}
          <Text fontSize={12} color="function.10" fontFamily={theme.fonts.body} lineHeight={16} strikeThrough={isEaten}>
            {formatMealType(mealMoment || "BREAKFAST", t)}, {getTotalRecipeTime(simpleRecipeMeal.simple_recipe)} mins
          </Text>
        </VStack>

        {/* Action Icons Container - Bottom Right */}
        <HStack position="absolute" bottom={3} right={3} space={2}>
          <IconButton
            onPress={onListAlternativesPress}
            icon={<RefreshCw size={theme.sizes["5"]} color={theme.colors.function[10]} strokeWidth={2} />}
            testID={`swap-simpleRecipeMeal-${simpleRecipeMeal.id}`}
            variant="ghost"
            borderRadius={theme.borderRadii.xs}
            size="sm"
            p={1.5}
          />

          {onPressCopyMeal ? (
            <IconButton
              onPress={onPressCopyMeal}
              icon={<Copy size={theme.sizes["5"]} color={theme.colors.function[10]} strokeWidth={2} />}
              testID={`copy-simpleRecipeMeal-${simpleRecipeMeal.id}`}
              variant="ghost"
              borderRadius={theme.borderRadii.xs}
              size="sm"
              p={1.5}
            />
          ) : null}

          <IconButton
            onPress={onLongPress}
            icon={<X size={theme.sizes["5"]} color={theme.colors.function[10]} strokeWidth={2} />}
            testID={`delete-simpleRecipeMeal-${simpleRecipeMeal.id}`}
            variant="ghost"
            borderRadius={theme.borderRadii.xs}
            size="sm"
            p={1.5}
          />
        </HStack>

        {/* Loading spinner overlay */}
        {isLoading ? (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            justifyContent="center"
            alignItems="center"
            bg="rgba(255, 255, 255, 0.8)"
          >
            <Spinner size="lg" color="primary.500" />
          </Box>
        ) : null}
      </Pressable>
    </Box>
  );
};

const SingleFoodOrQuickAddMealPlannerItem = ({
  isEaten,
  title,
  description,
  onLongPress,
  onPress,
  onPressCalendarItemPlannedStatus,
  testID,
  editable = true,
}: SingleFoodOrQuickAddMealPlannerItemProps): JSX.Element => {
  const theme = useTheme();
  return (
    <View
      style={{ flexDirection: "row", alignItems: "center", marginVertical: 8, paddingHorizontal: 4, marginTop: 32 }}
    >
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        style={{ flex: 1, flexDirection: "row", alignItems: "center" }}
        testID={testID}
        accessibilityRole="button"
      >
        <Text
          style={{
            fontWeight: "500",
            color: isEaten ? theme.colors.primary["300"] : theme.colors.gray["900"],
            textDecorationLine: isEaten ? "line-through" : "none",
            flex: 2,
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            fontWeight: "500",
            color: isEaten ? theme.colors.primary["300"] : theme.colors.gray["900"],
            textDecorationLine: isEaten ? "line-through" : "none",
            flex: 1,
          }}
        >
          {description}
        </Text>
      </Pressable>
      {editable ? (
        <View style={{ marginHorizontal: 10 }}>
          <CheckboxComponent
            onPress={() => onPressCalendarItemPlannedStatus && onPressCalendarItemPlannedStatus()}
            checked={isEaten}
            testID={`calendarItem-${isEaten ? "EATEN" : "PLANNED"}-singleFoodMealOrQuickAddMeal-NO_ID_PROVIDED`}
          />
        </View>
      ) : null}

      <IconButton
        icon={<Icon as={MaterialIcons} color={"primary.600"} name="delete" />}
        onPress={onLongPress}
        accessibilityLabel="Delete item"
        style={{ marginHorizontal: 10 }}
      />
    </View>
  );
};

const CalendarItemComponent = ({
  meal,
  status,
  contentType,
  onPress,
  onSwap,
  onLongPress,
  onPressCalendarItemPlannedStatus,
  onListAlternativesPress,
  editable = true,
  isLoading = false,
  mealMoment,
  onPressCopyMeal,
}: CalendarItemComponentProps): JSX.Element | null => {
  const { t } = useTranslation();
  const foodsById: FoodById = useSelector(foodSelector);
  const checkIsEatenStatus = (statusValue: CalendarItemStatusEnum): boolean => statusValue === "EATEN";

  const createSingleFoodMealPlannerItem = (): JSX.Element | null => {
    const singleFoodMeal: SingleFoodMeal = meal as SingleFoodMeal;
    let suggestedServing = singleFoodMeal.ingredient.suggested_serving;
    let foodName = singleFoodMeal.ingredient.name;

    if (typeof suggestedServing === "number") {
      const suggestedServingId = suggestedServing;
      // NOTE: Food is sometimes undefined when diaryscreen loads
      const food = findFoodBySuggestedServingId(suggestedServingId, foodsById);
      if (food) {
        foodName = food.name;
        suggestedServing = findMatchingSuggestedServing(food, suggestedServingId);
      }
    }

    if (suggestedServing) {
      const { nonEditableDescription } = getServingDescriptionText(suggestedServing, singleFoodMeal.ingredient);

      return (
        <SingleFoodOrQuickAddMealPlannerItem
          isEaten={checkIsEatenStatus(status)}
          title={foodName}
          description={nonEditableDescription}
          onPress={onPress}
          onLongPress={onLongPress}
          onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
          testID={`planned-singleFoodMeal-${singleFoodMeal.id || "MISSING_ID"}`}
          editable={editable}
        />
      );
    }
    return null;
  };

  const createQuickAddMealPlannerItem = (): JSX.Element | null => {
    const quickAddMeal: QuickAddMeal = meal as QuickAddMeal;

    return (
      <SingleFoodOrQuickAddMealPlannerItem
        isEaten={checkIsEatenStatus(status)}
        title={t("planner.item.quick_calories_title")}
        description={`${quickAddMeal.kcal} ${t("general.kcal")}`}
        onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
        onPress={onPress}
        onLongPress={onLongPress}
        testID={`planned-quickAddMeal-${quickAddMeal.id}`}
        editable={editable}
      />
    );
  };

  switch (contentType) {
    case "singlefoodmeal":
      return createSingleFoodMealPlannerItem();
    case "recipemeal":
      // eslint-disable-next-line no-case-declarations
      const recipeMeal: RecipeMeal = meal as RecipeMeal;

      if (!onPressCalendarItemPlannedStatus) {
        throw new Error("onPressCalendarItemPlannedStatus is required for RecipeMeal CalendarItem");
      }

      return (
        <RecipeMealPlannerItem
          recipeMeal={recipeMeal}
          isEaten={checkIsEatenStatus(status)}
          onPress={onPress}
          onSwap={onSwap}
          isLoading={isLoading}
          onLongPress={onLongPress}
          onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
          onListAlternativesPress={onListAlternativesPress}
          editable={editable}
          mealMoment={mealMoment}
          onPressCopyMeal={onPressCopyMeal}
        />
      );
    case "simplerecipemeal":
      // eslint-disable-next-line no-case-declarations
      const simpleRecipeMeal: SimpleRecipeMeal = meal as SimpleRecipeMeal;

      if (!onPressCalendarItemPlannedStatus) {
        throw new Error("onPressCalendarItemPlannedStatus is required for SimpleRecipeMeal CalendarItem");
      }

      return (
        <SimpleRecipeMealPlannerItem
          simpleRecipeMeal={simpleRecipeMeal}
          isEaten={checkIsEatenStatus(status)}
          onPress={onPress}
          onSwap={onSwap}
          isLoading={isLoading}
          onLongPress={onLongPress}
          onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
          onListAlternativesPress={onListAlternativesPress}
          editable={editable}
          mealMoment={mealMoment}
          onPressCopyMeal={onPressCopyMeal}
        />
      );
    case "quickaddmeal":
      return createQuickAddMealPlannerItem();
    default:
      return null;
  }
};

export default CalendarItemComponent;
